import type { SelectOption, SelectGroup } from "../ui/Select";

export type FieldKind =
  | "text"
  | "email"
  | "tel"
  | "number"
  | "date"
  | "select"
  | "textarea";

/** Internal option shape that pairs a canonical wire value with an i18n
 *  label key. `BookingFields` resolves the key via `t()` and emits a real
 *  `SelectOption` for `<Select>`. */
export type BookingSelectOption = { value: string; labelKey: string };

export type FieldSpec = {
  key: string;
  /** i18n key in the `booking` namespace. */
  labelKey: string;
  kind: FieldKind;
  requirement?: "required" | "optional";
  /** i18n key in the `booking` namespace; falls back to `selectPlaceholder`. */
  placeholderKey?: string;
  /** Translated, static option list (each `value` stays canonical EN so wire
   *  payloads + `revealWhen` keep matching across locales). */
  optionKeys?: BookingSelectOption[];
  /** Pre-built options (timezones) — used as-is, no translation. */
  options?: SelectOption[] | SelectGroup[];
  searchable?: boolean;
  maxLength?: number;
  /** Render only when another field equals this value (e.g. "Specific Date"). */
  revealWhen?: { key: string; equals: string };
  /** Span both columns of the desktop grid (textareas always do). */
  fullWidth?: boolean;
};

export type BookingShape = "live" | "inperson" | "events" | "sacred" | "digital";

export type FormValues = Record<string, string>;
/** Each value is an i18n key in the `booking` namespace (e.g. "fieldRequired"). */
export type FieldErrors = Record<string, string | undefined>;

export const SERVICE_SHAPE: Record<string, BookingShape> = {
  "live-tarot": "live",
  "live-psychic": "live",
  "live-mediumship": "live",
  "in-person": "inperson",
  events: "events",
  "tarot-corner": "events",
  "sacred-visit": "sacred",
  "digital-tarot-insight": "digital",
};

/** Pair a canonical EN value with its translated label key. */
const opt = (value: string, labelKey: string): BookingSelectOption => ({
  value,
  labelKey,
});

const NAME_EMAIL_PHONE = (phoneOptional = false): FieldSpec[] => [
  { key: "fullName", labelKey: "fullName", kind: "text", requirement: "required" },
  { key: "email", labelKey: "email", kind: "email", requirement: "required" },
  {
    key: "phone",
    labelKey: "phoneNumber",
    kind: "tel",
    requirement: phoneOptional ? "optional" : "required",
  },
];

export const BOOKING_FORMS: Record<BookingShape, FieldSpec[]> = {
  // Contact details only — date/time/timezone (and payment) are picked on
  // Calendly, where the Book button redirects (Service.calendlyUrl).
  live: [...NAME_EMAIL_PHONE()],
  inperson: [
    ...NAME_EMAIL_PHONE(),
    {
      key: "location",
      labelKey: "locationCity",
      kind: "text",
      requirement: "required",
    },
    {
      key: "preferredDate",
      labelKey: "preferredDate",
      kind: "date",
      requirement: "required",
    },
    {
      key: "preferredTime",
      labelKey: "preferredTime",
      kind: "text",
      requirement: "required",
    },
    {
      key: "notes",
      labelKey: "notes",
      kind: "textarea",
      requirement: "optional",
      maxLength: 600,
    },
  ],
  events: [
    ...NAME_EMAIL_PHONE(),
    {
      key: "eventType",
      labelKey: "eventType",
      kind: "select",
      requirement: "required",
      optionKeys: [
        opt("Private Event", "privateEvent"),
        opt("Corporate Event", "corporateEvent"),
        opt("Birthday", "birthday"),
        opt("Wedding", "wedding"),
        opt("Festival", "festival"),
        opt("Other", "other"),
      ],
    },
    { key: "location", labelKey: "location", kind: "text", requirement: "required" },
    {
      key: "eventDate",
      labelKey: "eventDate",
      kind: "date",
      requirement: "required",
    },
    {
      key: "expectedGuests",
      labelKey: "expectedGuests",
      kind: "number",
      requirement: "required",
    },
    {
      key: "bookingDuration",
      labelKey: "bookingDuration",
      kind: "text",
      requirement: "required",
    },
    {
      key: "additionalNotes",
      labelKey: "additionalNotes",
      kind: "textarea",
      requirement: "optional",
      maxLength: 600,
    },
  ],
  sacred: [
    ...NAME_EMAIL_PHONE(true),
    {
      key: "preferredDate",
      labelKey: "preferredDate",
      kind: "date",
      requirement: "required",
    },
    {
      key: "city",
      labelKey: "city",
      kind: "text",
      requirement: "required",
      fullWidth: true,
    },
    {
      key: "focus",
      labelKey: "focus",
      kind: "textarea",
      requirement: "required",
      maxLength: 600,
    },
    {
      key: "questions",
      labelKey: "questions",
      kind: "textarea",
      requirement: "optional",
      maxLength: 600,
    },
    {
      key: "notes",
      labelKey: "elseToKnow",
      kind: "textarea",
      requirement: "optional",
      maxLength: 600,
    },
  ],
  digital: [
    { key: "fullName", labelKey: "fullName", kind: "text", requirement: "required" },
    { key: "email", labelKey: "email", kind: "email", requirement: "required" },
    {
      key: "question",
      labelKey: "yourQuestion",
      kind: "textarea",
      requirement: "required",
      maxLength: 600,
    },
    {
      key: "context",
      labelKey: "optionalContext",
      kind: "textarea",
      requirement: "optional",
      maxLength: 600,
    },
  ],
};

export function visibleSpecs(specs: FieldSpec[], values: FormValues): FieldSpec[] {
  return specs.filter(
    (s) => !s.revealWhen || values[s.revealWhen.key] === s.revealWhen.equals,
  );
}

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export function validateBooking(
  specs: FieldSpec[],
  values: FormValues,
): FieldErrors {
  const errors: FieldErrors = {};
  for (const spec of visibleSpecs(specs, values)) {
    const value = (values[spec.key] ?? "").trim();
    if (spec.requirement !== "optional" && value === "") {
      errors[spec.key] = "fieldRequired";
      continue;
    }
    if (spec.kind === "email" && value !== "" && !EMAIL_RE.test(value)) {
      errors[spec.key] = "emailInvalid";
    }
  }
  return errors;
}

export function splitPayload(values: FormValues): {
  fullName: string;
  email: string;
  phone: string;
  formData: Record<string, string>;
} {
  const { fullName = "", email = "", phone = "", ...rest } = values;
  const formData: Record<string, string> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v != null && String(v).trim() !== "") formData[k] = v;
  }
  return {
    fullName: fullName.trim(),
    email: email.trim(),
    phone: phone.trim(),
    formData,
  };
}
